import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Select,
  Spinner,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DeleteRegular, SearchRegular } from '@fluentui/react-icons';
import { CATEGORIA_IFC_ES, type MemoriaTipoDetalle, type MemoriaTipoResumen, type Receta } from '@supernova/shared';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { listCatalogs } from '../api/catalogs.js';
import { borrarMemoria, detalleMemoria, editarMemoria, listarMemoria, restaurarMemoria } from '../api/memoriaTipos.js';
import { MEMORIA_TIPOS_ENABLED, usePlanoAHpVisible } from '../lib/planoGate.js';
import { queSeMideDe, recetaConMagnitud, recetaSinLinea, textoDeEstado, type QueSeMide } from '../lib/tiposDelDialogo.js';

const useStyles = makeStyles({
  pagina: { padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' },
  filtros: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  th: { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground3 },
  td: { padding: '6px 8px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}`, verticalAlign: 'top' },
  fila: { cursor: 'pointer', ':hover': { background: tokens.colorNeutralBackground1Hover } },
  nota: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  malo: { color: tokens.colorPaletteRedForeground1 },
  caja: { border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '6px' },
});

const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const claseLegible = (m: Pick<MemoriaTipoResumen, 'formato' | 'clase'>) =>
  m.formato === 'dwg' ? (m.clase === 'bloque' ? 'Bloque' : 'Capa') : (CATEGORIA_IFC_ES[m.clase] ?? m.clase);

const NOMBRE_DE_QUE_SE_MIDE: Record<string, string> = { conteo: 'Piezas', lineal: 'Largo', area: 'Área', volumen: 'Volumen', peso: 'Peso' };

/** Lo que falla de una receta, en una frase (null = vigente). */
function motivoPorRevisar(m: Pick<MemoriaTipoResumen, 'vigente' | 'lineasEstado'>): string | null {
  if (m.vigente) return null;
  const malo = m.lineasEstado.find((l) => l.estado !== 'vigente');
  return (malo && textoDeEstado(malo.estado)) ?? 'Hay que revisarla.';
}

/** Lo que recuerdas de tus planos: la lista con sus filtros. La usan la página y el modal del diálogo «Asignar conceptos a elementos». */
export function ConceptosAsignados() {
  const s = useStyles();
  const [formato, setFormato] = useState<'todos' | 'ifc' | 'dwg'>('todos');
  const [soloMias, setSoloMias] = useState(false);
  const [q, setQ] = useState('');
  const [qBuscada, setQBuscada] = useState('');
  const [items, setItems] = useState<MemoriaTipoResumen[] | null>(null);
  // Hay más filas que las mostradas (la lista llega por páginas): el cursor pide la siguiente.
  const [cursor, setCursor] = useState<string | null>(null);
  const [cargandoMas, setCargandoMas] = useState(false);
  // Solo vale la respuesta de la última consulta (al teclear en el buscador llegan varias).
  const consulta = useRef(0);
  const [error, setError] = useState<string | null>(null);
  const [catalogos, setCatalogos] = useState<Map<string, string>>(new Map());
  const [abierto, setAbierto] = useState<string | null>(null);
  // La lista es un árbol (formato → clase → tipo), como el diálogo; cada clase se puede cerrar.
  const [cerrados, setCerrados] = useState<ReadonlySet<string>>(new Set());
  const [porQuitar, setPorQuitar] = useState<MemoriaTipoResumen | null>(null);
  const [quitando, setQuitando] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQBuscada(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    void listCatalogs()
      .then((cs) => setCatalogos(new Map(cs.map((c) => [c.id, (c.displayName ?? '').trim() || c.name] as const))))
      .catch(() => undefined);
  }, []);

  const filtros = useCallback(
    () => ({
      ...(formato !== 'todos' ? { formato } : {}),
      estado: 'vigente' as const, // lo que ya no sirve o desapareció no se muestra
      soloMias,
      q: qBuscada,
    }),
    [formato, soloMias, qBuscada],
  );

  const cargar = useCallback(async () => {
    const mia = ++consulta.current;
    setError(null);
    try {
      const r = await listarMemoria(filtros());
      if (mia !== consulta.current) return;
      setItems(r.items);
      setCursor(r.cursor);
    } catch (e) {
      if (mia !== consulta.current) return;
      setError(e instanceof Error ? e.message : 'No se pudo leer lo que recuerdas.');
      setItems([]);
      setCursor(null);
    }
  }, [filtros]);

  const cargarMas = async () => {
    if (!cursor) return;
    const mia = consulta.current;
    setCargandoMas(true);
    try {
      const r = await listarMemoria({ ...filtros(), cursor });
      if (mia !== consulta.current) return;
      setItems((prev) => [...(prev ?? []), ...r.items]);
      setCursor(r.cursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer más.');
    } finally {
      setCargandoMas(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const arbol = useMemo(() => {
    const m = new Map<string, { formato: 'ifc' | 'dwg'; clase: string; items: MemoriaTipoResumen[] }>();
    for (const it of items ?? []) {
      const k = `${it.formato}|${claseLegible(it)}`;
      const g = m.get(k) ?? { formato: it.formato, clase: claseLegible(it), items: [] };
      g.items.push(it);
      m.set(k, g);
    }
    return [...m.entries()]
      .map(([k, g]) => ({ k, ...g }))
      .sort((a, b) => (a.formato === b.formato ? a.clase.localeCompare(b.clase, 'es') : a.formato === 'ifc' ? -1 : 1));
  }, [items]);
  const alternar = (k: string) =>
    setCerrados((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const quitar = async () => {
    if (!porQuitar) return;
    setQuitando(true);
    try {
      await borrarMemoria(porQuitar.id, porQuitar.version);
      setPorQuitar(null);
      await cargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar. Intenta de nuevo.');
      setPorQuitar(null);
    } finally {
      setQuitando(false);
    }
  };
  const hayFiltros = formato !== 'todos' || soloMias || qBuscada.trim().length > 0;

  return (
    <>
      <div className={s.pagina}>
        <h2 style={{ margin: 0 }}>Conceptos asignados</h2>
        <div className={s.nota}>Lo que asignaste en tus planos, ordenado como el modelo: primero la clase (muros, losas…) y dentro de ella cada tipo con su concepto. Se aplica solo en tus próximos planos con elementos iguales. Los precios no se guardan: salen siempre del catálogo vigente.</div>
        <div className={s.filtros}>
          <Input value={q} onChange={(_e, d) => setQ(d.value)} placeholder="Buscar por tipo, clase o concepto…" contentBefore={<SearchRegular />} aria-label="Buscar" style={{ minWidth: 280 }} />
          <Select value={formato} onChange={(_e, d) => setFormato(d.value as typeof formato)} aria-label="Formato">
            <option value="todos">Modelo 3D y dibujos</option>
            <option value="ifc">Modelo 3D (IFC)</option>
            <option value="dwg">Dibujos (DWG/DXF)</option>
          </Select>
          <Switch checked={soloMias} onChange={(_e, d) => setSoloMias(d.checked)} label="Solo las mías" />
        </div>
        {error && <div className={s.malo}>{error}</div>}
        {items === null ? (
          <Spinner label="Leyendo lo que recuerdas…" />
        ) : items.length === 0 ? (
          <div className={s.caja}>
            {hayFiltros
              ? 'No encontramos nada con esos filtros. Quita alguno para ver más.'
              : 'Todavía no hay nada guardado. Se guarda solo cuando asignas un concepto en «Asignar conceptos a elementos».'}
          </div>
        ) : (
          <table className={s.tabla}>
            <thead>
              <tr>
                {['Tipo', 'Concepto', 'Qué se mide', 'Aprendido en', 'Quién', 'Usos', ''].map((h) => (
                  <th key={h || 'acciones'} className={s.th}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {arbol.map((g, gi) => {
                const cerrado = cerrados.has(g.k);
                return (
                  <Fragment key={g.k}>
                    {(gi === 0 || arbol[gi - 1]!.formato !== g.formato) && (
                      <tr>
                        <td colSpan={7} className={s.td} style={{ fontWeight: 700, paddingTop: 12 }}>
                          {g.formato === 'ifc' ? 'Modelo 3D (IFC)' : 'Dibujos (DWG/DXF)'}
                        </td>
                      </tr>
                    )}
                    <tr className={s.fila} onClick={() => alternar(g.k)} style={{ background: tokens.colorNeutralBackground2 }}>
                      <td colSpan={7} className={s.td} style={{ fontWeight: 600 }}>
                        {cerrado ? '▸' : '▾'} {g.clase} <span className={s.nota}>· {g.items.length === 1 ? '1 tipo' : `${g.items.length} tipos`}</span>
                      </td>
                    </tr>
                    {!cerrado &&
                      g.items.map((m) => {
                        const principal = m.receta.lineas.find((l) => l.rol === 'principal');
                        const resto = m.receta.lineas.length - 1;
                        return (
                          <tr key={m.id} className={s.fila} onClick={() => setAbierto(m.id)}>
                            <td className={s.td} style={{ paddingLeft: 24 }}>{m.nivel === 'clase' ? 'Toda la clase' : m.nombre}</td>
                            <td className={s.td}>
                              {m.receta.noPresupuestar ? 'No se presupuesta' : principal ? `${principal.clave} · ${principal.descripcion}${resto > 0 ? ` (+${resto} complementarios)` : ''}` : '—'}
                              <div className={s.nota}>{m.receta.catalogoAncla ? catalogos.get(m.receta.catalogoAncla) ?? 'Otro catálogo' : ''}</div>
                            </td>
                            <td className={s.td}>{NOMBRE_DE_QUE_SE_MIDE[queSeMideDe(m.receta) ?? ''] ?? 'Automático'}</td>
                            <td className={s.td}>{m.aprendidoProyecto ?? '—'}</td>
                            <td className={s.td}>{m.porOtraPersona ?? (m.propia ? 'Tú' : '—')}</td>
                            <td className={s.td}>{m.usos}</td>
                            <td className={s.td}>
                              <Button
                                size="small"
                                appearance="subtle"
                                icon={<DeleteRegular />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPorQuitar(m);
                                }}
                              >
                                Quitar
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        {cursor && items && items.length > 0 && (
          <div>
            <Button appearance="secondary" disabled={cargandoMas} onClick={() => void cargarMas()}>
              {cargandoMas ? 'Cargando…' : 'Mostrar más'}
            </Button>
          </div>
        )}
      </div>
      {porQuitar && (
        <Dialog open onOpenChange={(_e, d) => { if (!d.open && !quitando) setPorQuitar(null); }}>
          <DialogSurface style={{ maxWidth: 480 }}>
            <DialogBody>
              <DialogTitle>¿Quitar esta asignación?</DialogTitle>
              <DialogContent>
                «{porQuitar.nivel === 'clase' ? `${claseLegible(porQuitar)} · toda la clase` : porQuitar.nombre}» dejará de aplicarse en tus próximos planos. No cambia ninguna hoja ya armada.
              </DialogContent>
              <DialogActions>
                <Button appearance="secondary" disabled={quitando} onClick={() => setPorQuitar(null)}>
                  Conservar
                </Button>
                <Button appearance="primary" disabled={quitando} onClick={() => void quitar()}>
                  {quitando ? 'Quitando…' : 'Sí, quitar'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
      {abierto && <DetalleDeMemoria id={abierto} catalogos={catalogos} onCerrar={() => setAbierto(null)} onCambio={() => void cargar()} />}
    </>
  );
}

function DetalleDeMemoria({ id, catalogos, onCerrar, onCambio }: { id: string; catalogos: Map<string, string>; onCerrar: () => void; onCambio: () => void }) {
  const s = useStyles();
  const [d, setD] = useState<MemoriaTipoDetalle | null>(null);
  const [receta, setReceta] = useState<Receta | null>(null);
  const [soloSugerir, setSoloSugerir] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  const recibir = (x: MemoriaTipoDetalle) => {
    setD(x);
    setReceta(x.receta);
    setSoloSugerir(x.soloSugerir);
  };
  useEffect(() => {
    void detalleMemoria(id).then(recibir).catch((e) => setError(e instanceof Error ? e.message : 'No se pudo abrir.'));
  }, [id]);

  const cambiado = useMemo(() => !!d && !!receta && (JSON.stringify(receta.lineas.map((l) => [l.clave, l.magnitud?.magnitud])) !== JSON.stringify(d.receta.lineas.map((l) => [l.clave, l.magnitud?.magnitud])) || soloSugerir !== d.soloSugerir), [d, receta, soloSugerir]);

  const correr = async (fn: () => Promise<MemoriaTipoDetalle | void>, cierra = false) => {
    setTrabajando(true);
    setError(null);
    try {
      const r = await fn();
      onCambio();
      if (cierra) onCerrar();
      else if (r) recibir(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo completar. Intenta de nuevo.');
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(_e, x) => { if (!x.open) onCerrar(); }}>
      <DialogSurface style={{ maxWidth: 820 }}>
        <DialogBody>
          <DialogTitle>{d ? (d.nivel === 'clase' ? `${claseLegible(d)} · toda la clase` : d.nombre) : 'Concepto asignado'}</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '70vh', overflowY: 'auto' }}>
            {!d || !receta ? (
              error ? <div className={s.malo}>{error}</div> : <Spinner />
            ) : (
              <>
                <div className={s.nota}>
                  {claseLegible(d)} · {d.formato === 'ifc' ? 'modelo 3D' : 'dibujo'} · versión {d.version}
                  {d.aprendidoProyecto ? ` · aprendido en «${d.aprendidoProyecto}»` : ''}
                </div>
                {motivoPorRevisar(d) && <div className={s.malo}>{motivoPorRevisar(d)} Para cambiar el concepto, asígnalo de nuevo desde un plano.</div>}
                {receta.noPresupuestar ? (
                  <div className={s.caja}>Este tipo no se presupuesta.</div>
                ) : (
                  <table className={s.tabla}>
                    <thead>
                      <tr>
                        {['Clave', 'Concepto', 'Unidad', 'Qué se mide', 'Rol', ''].map((h) => (
                          <th key={h} className={s.th}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {receta.lineas.map((l) => (
                        <tr key={l.orden}>
                          <td className={s.td}>{l.clave}</td>
                          <td className={s.td}>
                            {l.descripcion}
                            <div className={s.nota}>{catalogos.get(l.catalogId) ?? 'Otro catálogo'}</div>
                          </td>
                          <td className={s.td}>{l.unidad ?? '—'}</td>
                          <td className={s.td}>
                            {l.rol === 'principal' && d.formato === 'ifc' ? (
                              <Select size="small" value={queSeMideDe(receta) ?? 'auto'} aria-label="Qué se mide" onChange={(_e, x) => setReceta(recetaConMagnitud(receta, x.value === 'auto' ? null : (x.value as QueSeMide)))}>
                                <option value="auto">Automático</option>
                                {Object.entries(NOMBRE_DE_QUE_SE_MIDE).map(([k, v]) => (
                                  <option key={k} value={k}>
                                    {v}
                                  </option>
                                ))}
                              </Select>
                            ) : l.rol === 'principal' ? (
                              NOMBRE_DE_QUE_SE_MIDE[queSeMideDe(receta) ?? ''] ?? 'Automático'
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className={s.td}>{l.rol === 'principal' ? 'Principal' : 'Complementario'}</td>
                          <td className={s.td}>
                            {l.rol === 'complemento' && (
                              <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label={`Quitar ${l.clave}`} onClick={() => setReceta(recetaSinLinea(receta, l.orden) ?? receta)} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <Switch checked={soloSugerir} onChange={(_e, x) => setSoloSugerir(x.checked)} label="Preguntarme antes de usarlo en otros planos (se te sugiere, no se aplica solo)" />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Historia</div>
                  {d.versiones.map((v) => (
                    <div key={v.version} className={s.nota} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span>
                        Versión {v.version} · {fecha(v.cambiadoAt)} · {v.motivo}
                        {v.porOtraPersona ? ` · ${v.porOtraPersona}` : ''}
                      </span>
                      {v.version !== d.version && (
                        <Button size="small" appearance="subtle" disabled={trabajando} onClick={() => void correr(() => restaurarMemoria(d.id, v.version, d.version))}>
                          Volver a esta versión
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                {error && <div className={s.malo}>{error}</div>}
                {confirmarBorrar && (
                  <div className={s.caja}>
                    Se quita lo guardado y su historia: dejará de aplicarse en tus próximos planos. No cambia ninguna hoja ya armada.
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="small" appearance="primary" disabled={trabajando} onClick={() => void correr(() => borrarMemoria(d.id, d.version), true)}>
                        Sí, quitar
                      </Button>
                      <Button size="small" onClick={() => setConfirmarBorrar(false)}>
                        Conservar
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="subtle" disabled={!d || trabajando} onClick={() => setConfirmarBorrar(true)}>
              Quitar esta asignación
            </Button>
            <Button appearance="secondary" onClick={onCerrar}>
              Cerrar
            </Button>
            <Button appearance="primary" disabled={!d || !receta || !cambiado || trabajando} onClick={() => void correr(() => editarMemoria(d!.id, { receta: receta!, soloSugerir, version: d!.version }))}>
              Guardar cambios
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export function TiposRecordadosPage() {
  const visible = usePlanoAHpVisible();
  if (!MEMORIA_TIPOS_ENABLED || !visible) return <Navigate to="/projects" replace />;
  return (
    <>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Planos' }, { label: 'Conceptos asignados' }]} />
      <ConceptosAsignados />
    </>
  );
}
